import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BannerMarkdown from '../banners/BannerMarkdown'
import { POPUP_SEVERITY_CLASSES, type BannerSeverity } from '../banners/bannerSeverity'

interface BannerPopupModalProps {
  title: string
  message: string
  severity: BannerSeverity
  /** One-time popups show a "Don't show again" checkbox; recurrent do not. */
  oneTime: boolean
  /** Called when the user closes. `permanently` is true only when one-time + checkbox checked. */
  onClose: (permanently: boolean) => void
}

/**
 * Centered popup / modal banner (display_mode='popup'). Uses the Dreamwrite dark
 * design-system tokens (mirrors ui/Modal + the primary button): emoji + title,
 * markdown body, optional "Don't show again" checkbox (one-time only), and a
 * "Got it" button. Accessible: focus trap, Escape closes (= dismiss for this
 * pageview), labelled dialog, focus restore.
 */
export default function BannerPopupModal({
  title,
  message,
  severity,
  oneTime,
  onClose,
}: BannerPopupModalProps) {
  const { t } = useTranslation('workspace')
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const gotItRef = useRef<HTMLButtonElement>(null)
  const accent = POPUP_SEVERITY_CLASSES[severity]
  const SeverityIcon = accent.Icon

  // Keep the latest onClose in a ref so the keydown listener never needs it as a
  // dependency — otherwise the inline arrow from BannerHost (new identity each
  // render, e.g. when the checkbox toggles) would re-run the effect and steal
  // focus back to the "Got it" button mid-interaction.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Mount-only: focus the primary action and restore focus on unmount.
  useEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    gotItRef.current?.focus()
    return () => {
      previouslyFocused?.focus?.()
    }
  }, [])

  // Keydown: Escape closes (pageview dismiss) + Tab focus trap. Stable deps.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onCloseRef.current(false)
        return
      }
      if (e.key !== 'Tab' || !dialogRef.current) return
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, input, [href], [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4"
      role="presentation"
      data-testid="banner-popup-overlay"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="banner-popup-title"
        data-testid="banner-popup"
        data-severity={severity}
        className="w-full max-w-md rounded-lg border border-border bg-surface-1 p-6 text-foreground shadow-lg"
      >
        <div className="flex items-start justify-between gap-3">
          <h2
            id="banner-popup-title"
            className={`flex items-center gap-2 text-lg font-semibold ${accent.accent}`}
          >
            <SeverityIcon aria-hidden="true" className="h-5 w-5 flex-shrink-0" />
            <span>{title}</span>
          </h2>
          <button
            type="button"
            onClick={() => onClose(false)}
            aria-label={t('banners.close')}
            data-testid="banner-popup-close"
            className="flex-shrink-0 rounded p-1 text-muted-foreground hover:bg-surface-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            ×
          </button>
        </div>

        <div
          className="mt-3 text-sm leading-relaxed text-foreground/80"
          data-testid="banner-popup-message"
        >
          <BannerMarkdown>{message}</BannerMarkdown>
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          {oneTime ? (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                data-testid="banner-popup-dont-show-again"
                className="h-4 w-4 rounded border-input-border bg-input-bg accent-primary text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              />
              {t('banners.dontShowAgain')}
            </label>
          ) : (
            <span />
          )}
          <button
            ref={gotItRef}
            type="button"
            onClick={() => onClose(oneTime && dontShowAgain)}
            data-testid="banner-popup-got-it"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
          >
            {t('banners.gotIt')}
          </button>
        </div>
      </div>
    </div>
  )
}
